import React from "react";
import { useTranslation } from "react-i18next";
import { useCompany } from "../Company/CompanyContext";

const Star = () => <span className="text-error font-bold ml-0.5">*</span>;

// Normalise en YYYY-MM-DD pour <input type="date">
const toYMD = (val) => {
  if (!val) return "";
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch { return ""; }
};


export default function QuoteClientInfo({ formData, handleChange }) {
  const { t } = useTranslation();
  const { prestationTypes, paymentMethods } = useCompany();

  return (
    <div className="bg-base-200/50 rounded-xl p-4 border border-base-200 space-y-4">
      <h3 className="text-sm font-bold text-base-content/60 uppercase tracking-wider">
        {t("quote.infoAndCalc", "Information & Calculs")}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Nom client (requis) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            {t("quote.clientName")} <Star />
          </label>
          <input
            type="text"
            name="client_name"
            value={formData.client_name}
            onChange={handleChange}
            className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            placeholder={t("quote.clientName")}
            required
            autoComplete="name"
          />
        </div>

        {/* Téléphone (facultatif) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            {t("quote.clientPhone")}
          </label>
          <input
            type="tel"
            name="client_phone"
            value={formData.client_phone}
            onChange={handleChange}
            className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            placeholder="+33 6 00 00 00 00"
            autoComplete="tel"
            inputMode="tel"
          />
        </div>

        {/* Email (facultatif) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            {t("quote.clientEmail")}
          </label>
          <input
            type="email"
            name="client_email"
            value={formData.client_email}
            onChange={handleChange}
            className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            placeholder="client@email.com"
            autoComplete="email"
          />
        </div>

        {/* Date (requis) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            {t("quote.date")} <Star />
          </label>
          <input
            type="date"
            id="date"
            name="date"
            value={toYMD(formData?.date)}
            onChange={handleChange}
            className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            required
          />
        </div>

        {/* Adresse (requis) */}
        <div className="flex flex-col gap-1.5 md:col-span-2">
          <label className="text-sm font-semibold text-base-content/70">
            {t("quote.clientAddress")} <Star />
          </label>
          <input
            type="text"
            name="client_address"
            value={formData.client_address}
            onChange={handleChange}
            className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            placeholder={t("quote.clientAddress")}
            required
            autoComplete="street-address"
          />
        </div>

        {/* Ville (requis) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            {t("quote.clientCity")} <Star />
          </label>
          <input
            type="text"
            name="client_city"
            value={formData.client_city}
            onChange={handleChange}
            className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            placeholder={t("quote.clientCity")}
            required
            autoComplete="address-level2"
          />
        </div>

        {/* Code postal client (facultatif) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            {t("quote.clientZip", "Code postal")}
          </label>
          <input
            type="text"
            name="client_zip"
            value={formData.client_zip || ""}
            onChange={handleChange}
            className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            placeholder="75015"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={5}
          />
        </div>

        {/* Type de prestation (requis) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            {t("quote.prestationType")} <Star />
          </label>
          <select
            name="prestation_type"
            value={formData.prestation_type}
            onChange={handleChange}
            className="select select-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            required
          >
            <option value="">{t("quote.selectPrestationType", "Choisir un type de prestation")}</option>
            {prestationTypes.map((pt) => (
              <option key={pt} value={pt}>{pt}</option>
            ))}
          </select>
        </div>

        {/* Moyen de paiement (facultatif) */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-base-content/70">
            {t("quote.paymentMethod", "Moyen de paiement")}
          </label>
          <select
            name="payment_method"
            value={formData.payment_method || ""}
            onChange={handleChange}
            className="select select-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
          >
            <option value="">{t("quote.selectPaymentMethod", "Choisir un moyen de paiement")}</option>
            {paymentMethods.map((pm) => (
              <option key={pm} value={pm}>{pm}</option>
            ))}
          </select>
        </div>

      </div>
    </div>
  );
}
